import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { ActionButton, FormDialog, InputField, SelectField } from "../v3";
import { get_date } from "../generalComponets";
import { AccountService, PerformanceGuaranteesService } from "../../services";
import { addReturnValidation } from "../../pages/performanceGuarantees/validation";
import { formatPayload } from "../../utils";
import moment from "moment";
import { useSelector } from "react-redux";
import { renameCashAccount } from "../../utils/accountUtils";

export const AddReturn = ({ pgData = null, open = false, onClose = () => {}, onSuccess = () => {} }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [pgDetails, setPgDetails] = useState({
    releaseAmount: undefined,
    toAccountId: undefined,
    releaseDate: get_date(Date.now(), "YYYY-MM-DD"),
    description: undefined,
  });

  const { user } = useSelector((state) => state.user);
  const { ownerUserId: companyAdminId } = user.company;

  useEffect(() => {
    if (open && pgData) {
      setPgDetails({
        releaseAmount: Number(pgData?.depositAmount),
        toAccountId: pgData?.depositTransaction?.fromAccountId,
        releaseDate: get_date(Date.now(), "YYYY-MM-DD"),
        description: undefined,
      });
      fetchData(pgData?.depositTransaction?.fromAccountId);
    }
  }, [open, pgData]);

  const fetchData = async (accountId) => {
    try {
      const { data: response } = await AccountService.getAccountById(accountId);
      setSelectedAccount(renameCashAccount(response, user, companyAdminId));
    } catch (error) {
      console.error("Error fetching account details:", error);
      toast.error("Failed to fetch account details.");
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setPgDetails((prev) => ({ ...prev, [name]: value }));

    if (name === "releaseAmount") {
      const num = parseFloat(value);
      if (value !== "" && (isNaN(num) || num < pgData?.depositAmount)) {
        setErrors((prev) => ({
          ...prev,
          [name]: "Amount must be equal to or greater than " + pgData?.depositAmount,
        }));
      } else {
        setErrors((prev) => {
          const rest = { ...prev };
          delete rest[name];
          return rest;
        });
      }
    }
  };

  const handleCloseModal = () => {
    setErrors({});
    setPgDetails({
      releaseAmount: undefined,
      toAccountId: undefined,
      releaseDate: get_date(Date.now(), "YYYY-MM-DD"),
      description: undefined,
    });
    setSelectedAccount(null);
    onClose();
  };

  const handleSave = async () => {
    setErrors({});
    const formattedData = formatPayload(pgDetails);

    const validationErrors = addReturnValidation(formattedData, pgData?.depositAmount);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formattedData,
        releaseAmount: Number(formattedData.releaseAmount),
        releaseDate: moment(formattedData.releaseDate).toISOString(),
      };
      await PerformanceGuaranteesService.addReturn(pgData.id, payload);
      toast.success("Return added successfully.");
      handleCloseModal();
      onSuccess?.();
    } catch (error) {
      console.error("Error adding return:", error);
      toast.error("Failed to add return.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <FormDialog
      open={open}
      title="Add Return"
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSave} disabled={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <Box sx={{ width: 250 }}>
            <InputField
              required
              label="Amount"
              name="releaseAmount"
              value={pgDetails.releaseAmount}
              onChange={updateDetailsState}
              error={!!errors.releaseAmount}
              helperText={errors.releaseAmount}
              fullWidth
            />
          </Box>
          <Box sx={{ width: 250 }}>
            <SelectField
              label="Account"
              value={selectedAccount}
              getData={AccountService.getAccounts}
              required
              disabled
              error={!!errors.toAccountId}
              helperText={errors.toAccountId}
              style={{ width: "100%" }}
            />
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            required
            type="date"
            label="Release Date"
            name="releaseDate"
            value={pgDetails?.releaseDate}
            onChange={updateDetailsState}
            error={!!errors.releaseDate}
            helperText={errors.releaseDate}
            style={{ width: "100%" }}
          />
          <InputField
            label="Description"
            name="description"
            value={pgDetails.description}
            onChange={updateDetailsState}
            error={!!errors.description}
            helperText={errors.description}
            fullWidth
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
